// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// Copyright (c) Utrecht University (Department of Information and Computing Sciences)

import SimplexNoise from "simplex-noise";
import { clamp } from "@/utils/worldGeneration/default/WorldGen";

/**
 * Generate a 2D simplex noise
 * @param seed Randomness number
 * @param width Noise width
 * @param height Noise height
 * @param frequency Noise frequency
 * @param amplitude Noise amplitude
 * @returns Noise
 */
function simplexNoise(
  seed: number,
  width: number,
  height: number,
  frequency: number,
  amplitude: number
): number[][] {
  const simplex = new SimplexNoise(seed);
  const noise: number[][] = [];
  for (let x = 0; x < width; x++) {
    noise[x] = [];
    for (let y = 0; y < height; y++) {
      noise[x][y] = simplex.noise2D(x / frequency, y / frequency) * 0.5 + 0.5;
      noise[x][y] *= amplitude;
    }
  }

  return noise;
}

/**
 * Generate tv snow
 * @param seed Randomness number
 * @param width Noise width
 * @param height Noise height
 * @returns Noise
 */
function valueNoise(seed: string, width: number, height: number): number[][] {
  const noise: number[][] = [];
  for (let x = 0; x < width; x++) {
    noise[x] = [];
    for (let y = 0; y < height; y++) {
      noise[x][y] = Math.random();
    }
  }
  return noise;
}

/**
 * Use multiple octaves to generate multiple noise functions and collapse them into a single @d noise function
 * @param seed Randomness number
 * @param width Noise width
 * @param height Noise height
 * @param lacunarity Noise lacunarity
 * @param persistence Noise persistence
 * @param octaves Noise octaves
 * @returns Noise
 */
function getCollapsedNoise(
  seed: number,
  width: number,
  height: number,
  lacunarity: number,
  persistence: number,
  octaves: number
): number[][] {
  const noises: number[][][] = [];
  for (let i = 0; i < octaves; i++) {
    const frequency = lacunarity / Math.pow(lacunarity, i);
    const amplitude = Math.pow(persistence, i);
    noises[i] = simplexNoise(seed, width, height, frequency, amplitude);
  }
  const noise: number[][] = waveFunctionCollapse(noises);

  return noise;
}

/**
 * Collapses multiple noise functions into one
 * @param args Wave
 * @returns Noise
 */
function waveFunctionCollapse(args: number[][][]): number[][] {
  const width = args[0].length;
  const height = args[0][0].length;

  //Check if everything has the same size
  if (
    !args.every(
      (arg: number[][]) => arg.length == width && arg[0].length == height
    )
  ) {
    throw new Error("incompatible array sizes");
  }

  const noise: number[][] = [];

  for (let x = 0; x < width; x++) {
    noise[x] = [];
    for (let y = 0; y < height; y++) {
      let val = 0;
      //Collapse
      for (let i = 0; i < args.length; i++) val += args[i][x][y];

      //Normalize
      noise[x][y] = clamp(val);
    }
  }

  return noise;
}

export { getCollapsedNoise, simplexNoise, valueNoise };
